import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { fetchWrapper } from "_helpers";

// create slice

const name = "orders";
const initialState = createInitialState();
const extraActions = createExtraActions();
const extraReducers = createExtraReducers();
const slice = createSlice({ name, initialState, extraReducers });

// exports

export const ordersActions = { ...slice.actions, ...extraActions };
export const ordersReducer = slice.reducer;

// implementation

function createInitialState() {
    return {
    };
}

function createExtraActions() {
    const baseUrl = `${process.env.REACT_APP_API_URL}/orders`;

    return {
        createPurchase: createPurchase(),
        createSale: createSale(),
        createTransportService: createTransportService(),
        createSeries: createSeries(),
        updateTransportDetails: updateTransportDetails(),
        updateTransportDetailsBatch: updateTransportDetailsBatch(),
        updateSeriesTransportDetails: updateSeriesTransportDetails(),
        updateBdoDetails: updateBdoDetails(),
        updateWastesDetails: updateWastesDetails(),
        updateSeriesWastesDetails: updateSeriesWastesDetails(),
        updateMainDetails: updateMainDetails(),
        updateSeriesMainDetails: updateSeriesMainDetails(),
        confirmOrder: confirmOrder(),
        completeCourse: completeCourse(),
        confirmOrderReceive: confirmOrderReceive(),
        confirmOrderTransport: confirmOrderTransport(),
        closeOrder: closeOrder(),
        cancel: cancel(),
        reopen: reopen(),
        reject: reject(),
        deleteOrder: deleteOrder(),
        recoverOrder: recoverOrder(),
        undoConfirmOrder: undoConfirmOrder(),
        deleteSeries: deleteSeries(),
        refreshKpoCards: refreshKpoCards(),
        refreshKpoCard: refreshKpoCard(),
        linkKpoToOrder: linkKpoToOrder(),
        unlinkKpoFromOrder: unlinkKpoFromOrder(),
        rejectKpoWithWastesDetails: rejectKpoWithWastesDetails(),
        getOrderWastesBdo: getOrderWastesBdo(),
        updateOrderDriverExcludedKpoIds: updateOrderDriverExcludedKpoIds(),
        getRatePlanForOrder: getRatePlanForOrder(),
        getRatePlansForOrder: getRatePlansForOrder(),
        attachRatePlanToOrder: attachRatePlanToOrder(),
        refreshKeoEntriesForKpoCards: refreshKeoEntriesForKpoCards(),
        attachWeighbridgeTickets: attachWeighbridgeTickets(),
        searchExternalWeighingTickets: searchExternalWeighingTickets(),
        getExternalWeighingTicketDetails: getExternalWeighingTicketDetails(),
        getExternalWeighingTicketDetailsBatch: getExternalWeighingTicketDetailsBatch(),
        getQuickWeighedWastes: getQuickWeighedWastes(),
        searchExternalWeighingContractors: searchExternalWeighingContractors(),
    };

    function createPurchase() {
        return createAsyncThunk(`${name}/create-purchase`, async (payload) => {
            return await fetchWrapper.post(`${baseUrl}/create-purchase`, payload);
        });
    }

    function createSale() {
        return createAsyncThunk(`${name}/sale`, async (payload) => {
            return await fetchWrapper.post(`${baseUrl}/sale`, payload);
        });
    }

    function createTransportService() {
        return createAsyncThunk(`${name}/transport-service`, async (payload) => {
            return await fetchWrapper.post(`${baseUrl}/transport-service`, payload);
        });
    }

    function createSeries() {
        return createAsyncThunk(`${name}/create-series`, async (payload) => {
            return await fetchWrapper.post(`${baseUrl}/series`, payload);
        });
    }

    function updateTransportDetails() {
        return createAsyncThunk(`${name}/updateTransportDetails`, async ({ orderId, payload }) => {
            return await fetchWrapper.put(`${baseUrl}/${orderId}/transport-details`, payload);
        });
    }

    function updateTransportDetailsBatch() {
        return createAsyncThunk(`${name}/updateTransportDetailsBatch`, async ({ payload }) => {
            return await fetchWrapper.post(`${baseUrl}/transport-details/batch`, payload);
        });
    }

    function updateSeriesTransportDetails() {
        return createAsyncThunk(`${name}/updateTransportDetails`, async ({ seriesId, payload }) => {
            return await fetchWrapper.put(`${baseUrl}/series/${seriesId}/transport-details`, payload);
        });
    }

    function updateBdoDetails() {
        return createAsyncThunk(`${name}/updateBdoDetails`, async ({ orderId, payload }) => {
            return await fetchWrapper.put(`${baseUrl}/${orderId}/bdo-details`, payload);
        });
    }

    function updateWastesDetails() {
        return createAsyncThunk(`${name}/updateWastesDetails`, async ({ orderId, payload }) => {
            return await fetchWrapper.put(`${baseUrl}/${orderId}/wastes-details`, payload);
        });
    }

    function updateSeriesWastesDetails() {
        return createAsyncThunk(`${name}/updateSeriesWastesDetails`, async ({ seriesId, payload }) => {
            return await fetchWrapper.put(`${baseUrl}/series/${seriesId}/wastes-details`, payload);
        });
    }

    function updateMainDetails() {
        return createAsyncThunk(`${name}/updateMainDetails`, async ({ orderId, payload }) => {
            return await fetchWrapper.put(`${baseUrl}/${orderId}/main-details`, payload);
        });
    }
    function updateSeriesMainDetails() {
        return createAsyncThunk(`${name}/updateSeriesMainDetails`, async ({ seriesId, payload }) => {
            return await fetchWrapper.put(`${baseUrl}/series/${seriesId}/main-details`, payload);
        });
    }


    function rejectKpoWithWastesDetails() {
        return createAsyncThunk(`${name}/rejectKpoWithWastesDetails`, async ({ orderId, payload }) => {
            return await fetchWrapper.put(`${baseUrl}/${orderId}/wastes-details/reject-kpo`, payload);
        });
    }

    function confirmOrder() {
        return createAsyncThunk(`${name}/confirmOrder`, async ({ orderId }) => {
            return await fetchWrapper.patch(`${baseUrl}/${orderId}/confirm`);
        });
    }

    function completeCourse() {
        return createAsyncThunk(`${name}/completeCourse`, async ({ orderId, payload }) => {
            const { skipServiceValidation } = payload;
            return await fetchWrapper.patch(`${baseUrl}/${orderId}/complete-course?skipServiceValidation=${skipServiceValidation ?? false}`, payload);
        });
    }

    function confirmOrderReceive() {
        return createAsyncThunk(`${name}/confirmOrderReceive`, async ({ orderId }) => {
            return await fetchWrapper.patch(`${baseUrl}/${orderId}/confirm-receive`);
        });
    }

    function confirmOrderTransport() {
        return createAsyncThunk(`${name}/confirmOrderTransport`, async ({ orderId }) => {
            return await fetchWrapper.patch(`${baseUrl}/${orderId}/confirm-transport`);
        });
    }

    function closeOrder() {
        return createAsyncThunk(`${name}/closeOrder`, async ({ orderId, payload }) => {
            const { skipWasteValidation } = payload;
            return await fetchWrapper.patch(`${baseUrl}/${orderId}/close?skipWasteValidation=${skipWasteValidation ?? false}`, payload);
        });
    }

    function cancel() {
        return createAsyncThunk(`${name}/cancel`, async ({ orderId, payload }) => {
            return await fetchWrapper.patch(`${baseUrl}/${orderId}/cancel`, { ...payload, id: orderId });
        });
    }

    function reopen() {
        return createAsyncThunk(`${name}/reopen`, async ({ orderId }) => {
            return await fetchWrapper.patch(`${baseUrl}/${orderId}/reopen`);
        });
    }

    function reject() {
        return createAsyncThunk(`${name}/reject`, async ({ orderId }) => {
            return await fetchWrapper.patch(`${baseUrl}/${orderId}/reject`);
        });
    }

    function deleteOrder() {
        return createAsyncThunk(`${name}/delete`, async ({ orderId }) => {
            return await fetchWrapper.delete(`${baseUrl}/${orderId}`);
        });
    }

    function recoverOrder() {
        return createAsyncThunk(`${name}/recover`, async ({ orderId }) => {
            return await fetchWrapper.patch(`${baseUrl}/${orderId}/recover`);
        });
    }

    function undoConfirmOrder() {
        return createAsyncThunk(`${name}/undo-confirm`, async ({ orderId }) => {
            return await fetchWrapper.patch(`${baseUrl}/${orderId}/undo-confirm`);
        });
    }

    function deleteSeries() {
        return createAsyncThunk(`${name}/deleteSeries`, async ({ orderSeriesId }) => {
            return await fetchWrapper.delete(`${baseUrl}/series/${orderSeriesId}`);
        });
    }

    function refreshKpoCards() {
        return createAsyncThunk(`${name}/refreshKpoCards`, async ({ orderId, transactionType }) => {
            return await fetchWrapper.patch(`${baseUrl}/${orderId}/kpo/refresh-all?transactionType=${transactionType}`);
        });
    }

    function refreshKpoCard() {
        return createAsyncThunk(`${name}/refreshKpoCard`, async ({ orderId, kpoId, transactionType }) => {
            return await fetchWrapper.patch(`${baseUrl}/${orderId}/kpo/${kpoId}/refresh?transactionType=${transactionType}`);
        });
    }

    function linkKpoToOrder() {
        return createAsyncThunk(
          `${name}/linkToOrder`,
          async({ kpoId, orderId, status }) =>
            await fetchWrapper.put(`${baseUrl}/${orderId}/kpo/${kpoId}/link?status=${status}`));
    }

    function unlinkKpoFromOrder() {
        return createAsyncThunk(
          `${name}/unlinkFromOrder`,
          async({ kpoId, orderId }) =>
            await fetchWrapper.put(`${baseUrl}/${orderId}/kpo/${kpoId}/unlink`));
    }

    function getOrderWastesBdo() {
        return createAsyncThunk(
          `${name}/getOrderWastesBdo`,
          async({ orderId }) =>
            await fetchWrapper.get(`${baseUrl}/${orderId}/wastes-bdo`));
    }

    function updateOrderDriverExcludedKpoIds() {
        return createAsyncThunk(
          `${name}/updateOrderDriverExcludedKpoIds`,
          async({ orderId, driverExcludedKpoIds }) =>
            await fetchWrapper.put(`${baseUrl}/${orderId}/driver-kpo-excluded`, { orderId, driverExcludedKpoIds }));
    }

    function getRatePlanForOrder() {
        return createAsyncThunk(
          `${name}/getRatePlanForOrder`,
          async({ orderId }) =>
            await fetchWrapper.get(`${baseUrl}/${orderId}/rate-plan/preview`));
    }

    function getRatePlansForOrder() {
        return createAsyncThunk(
          `${name}/getRatePlansForOrder`,
          async({ orderId }) =>
            await fetchWrapper.get(`${baseUrl}/${orderId}/rate-plans/preview`));
    }

    function attachRatePlanToOrder() {
        return createAsyncThunk(
          `${name}/attachRatePlanToOrder`,
          async({ orderId, payload }) =>
            await fetchWrapper.post(`${baseUrl}/${orderId}/rate-plan`, payload));
    }

    function refreshKeoEntriesForKpoCards() {
        return createAsyncThunk(
          `${name}/refreshKeoEntriesForKpoCards`,
          async({ orderId, payload }) =>
            await fetchWrapper.post(`${baseUrl}/${orderId}/kpo/keo/refresh-entries`, payload));
    }

    function attachWeighbridgeTickets() {
        return createAsyncThunk(
          `${name}/attachWeighbridgeTickets`,
          async({ orderId, payload }) =>
            await fetchWrapper.post(`${baseUrl}/${orderId}/weighbridge-tickets`, payload));
    }

    function searchExternalWeighingTickets() {
        const externalBaseUrl = `${process.env.REACT_APP_API_URL}/external-weighing-tickets`;
        return createAsyncThunk(
          `${name}/searchExternalWeighingTickets`,
          async({ query }) =>
            await fetchWrapper.post(`${externalBaseUrl}/search`, query));
    }

    function getExternalWeighingTicketDetails() {
        const externalBaseUrl = `${process.env.REACT_APP_API_URL}/external-weighing-tickets`;
        return createAsyncThunk(
          `${name}/getExternalWeighingTicketDetails`,
          async({ externalTicketId }) =>
            await fetchWrapper.get(`${externalBaseUrl}/${externalTicketId}`));
    }

    function getExternalWeighingTicketDetailsBatch() {
        const externalBaseUrl = `${process.env.REACT_APP_API_URL}/external-weighing-tickets`;
        return createAsyncThunk(
          `${name}/getExternalWeighingTicketDetailsBatch`,
          async({ externalTicketIds }) =>
            await fetchWrapper.post(`${externalBaseUrl}/batch`, { externalTicketIds }));
    }

    function getQuickWeighedWastes() {
        const externalBaseUrl = `${process.env.REACT_APP_API_URL}/external-weighing-tickets`;
        return createAsyncThunk(
          `${name}/getQuickWeighedWastes`,
          async({ searchQuery }) =>
            await fetchWrapper.get(`${externalBaseUrl}/quick-weighed-wastes?id=${searchQuery ?? ""}`));
    }

    function searchExternalWeighingContractors() {
        const externalBaseUrl = `${process.env.REACT_APP_API_URL}/external-weighing-tickets`;
        return createAsyncThunk(
          `${name}/searchExternalWeighingContractors`,
          async({ searchQuery }) =>
            await fetchWrapper.get(`${externalBaseUrl}/contractors/search?searchQuery=${searchQuery ?? ""}`));
    }
}

function createExtraReducers() {
    return {

    };
}
